/**
 * Add Material Ticket Dialog Component - Redesigned
 * Simplified workflow: Simple mode by default, advanced mode optional
 */

import { useState, useEffect } from 'react';
import type {
  DestinationType,
  WeightUnit,
  TicketMaterialPercentage
} from './types';

interface AddTicketDialogProps {
  jobId: string;
  open: boolean;
  onClose: () => void;
  onTicketAdded?: () => void;
}

const DESTINATION_TYPES: { value: DestinationType; label: string; diverts: boolean }[] = [
  { value: 'recycling', label: 'Recycling Center', diverts: true },
  { value: 'landfill', label: 'Landfill', diverts: false },
  { value: 'donation', label: 'Donation Center', diverts: true },
  { value: 'composting', label: 'Composting Facility', diverts: true },
  { value: 'salvage', label: 'Salvage Yard', diverts: true },
];

const COMMON_MATERIALS = [
  'C&D (Mixed Construction & Demolition)',
  'Mixed Waste',
  'Wood',
  'Concrete',
  'Metal',
  'Drywall',
  'Asphalt',
  'Cardboard',
  'Other'
];

export function AddTicketDialog({ jobId, open, onClose, onTicketAdded }: AddTicketDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'simple' | 'advanced'>('simple');

  // Basic fields
  const [ticketDate, setTicketDate] = useState(new Date().toISOString().split('T')[0] || '');
  const [ticketNumber, setTicketNumber] = useState('');
  const [haulerName, setHaulerName] = useState('');
  const [facilityName, setFacilityName] = useState('');
  const [destinationType, setDestinationType] = useState<DestinationType | ''>('');
  const [notes, setNotes] = useState('');

  // Simple mode fields
  const [totalWeight, setTotalWeight] = useState('');
  const [weightUnit, setWeightUnit] = useState<WeightUnit>('tons');
  const [materialType, setMaterialType] = useState('');
  const [diversionPercentage, setDiversionPercentage] = useState('');

  // Advanced mode fields
  const [materials, setMaterials] = useState<TicketMaterialPercentage[]>([
    { material_type: '', percentage: 0, is_diverted: true }
  ]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setTicketDate(new Date().toISOString().split('T')[0] || '');
      setTicketNumber('');
      setHaulerName('');
      setFacilityName('');
      setDestinationType('');
      setNotes('');
      setTotalWeight('');
      setWeightUnit('tons');
      setMaterialType('');
      setDiversionPercentage('');
      setMaterials([{ material_type: '', percentage: 0, is_diverted: true }]);
      setMode('simple');
      setError(null);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const weight = parseFloat(totalWeight);
      if (isNaN(weight) || weight <= 0) {
        throw new Error('Please enter a valid weight');
      }

      if (!haulerName.trim()) {
        throw new Error('Hauler name is required');
      }

      if (!facilityName.trim()) {
        throw new Error('Facility name is required');
      }

      if (!destinationType) {
        throw new Error('Destination type is required');
      }

      if (mode === 'simple' && !materialType.trim()) {
        throw new Error('Material type is required');
      }

      if (mode === 'advanced') {
        const totalPercentage = materials.reduce((sum, m) => sum + m.percentage, 0);
        if (Math.abs(totalPercentage - 100) > 0.01) {
          throw new Error('Material percentages must add up to 100%');
        }
        if (materials.some(m => !m.material_type.trim())) {
          throw new Error('All materials must have a type');
        }
      }

      // Build request based on mode
      interface TicketRequestData {
        ticket_date: number;
        ticket_number?: string;
        hauler_name: string;
        facility_name: string;
        destination_type: DestinationType;
        notes?: string;
        weight_unit: WeightUnit;
        total_weight: number;
        allocation_method: string;
        material_type?: string;
        diversion_percentage?: number;
        material_percentages?: TicketMaterialPercentage[];
      }

      const ticketData: TicketRequestData = {
        ticket_date: new Date(ticketDate).getTime() / 1000,
        ticket_number: ticketNumber || undefined,
        hauler_name: haulerName,
        facility_name: facilityName,
        destination_type: destinationType,
        notes: notes || undefined,
        weight_unit: weightUnit,
        total_weight: weight,
        allocation_method: '', // Set below based on mode
      };

      if (mode === 'simple') {
        ticketData.allocation_method = 'simple';
        ticketData.material_type = materialType;
        if (diversionPercentage) {
          const divPct = parseFloat(diversionPercentage);
          if (!isNaN(divPct) && divPct >= 0 && divPct <= 100) {
            ticketData.diversion_percentage = divPct;
          }
        }
      } else {
        ticketData.allocation_method = 'manual_breakdown';
        ticketData.material_percentages = materials;
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/jobs/${jobId}/tickets`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(ticketData),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to add ticket');
      }

      if (onTicketAdded) {
        onTicketAdded();
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add ticket');
    } finally {
      setIsLoading(false);
    }
  };

  const addMaterial = () => {
    setMaterials([...materials, { material_type: '', percentage: 0, is_diverted: true }]);
  };

  const removeMaterial = (index: number) => {
    setMaterials(materials.filter((_, i) => i !== index));
  };

  const updateMaterial = (index: number, field: keyof TicketMaterialPercentage, value: string | number | boolean) => {
    const updated = [...materials];
    const current = updated[index];
    if (current) {
      updated[index] = { ...current, [field]: value } as TicketMaterialPercentage;
    }
    setMaterials(updated);
  };

  const totalPercentage = materials.reduce((sum, m) => sum + m.percentage, 0);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-neutral-200 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-neutral-900">Add Material Ticket</h2>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-600"
            type="button"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
              {error}
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="font-semibold text-neutral-900">Ticket Information</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Ticket Date *
                </label>
                <input
                  type="date"
                  value={ticketDate}
                  onChange={(e) => setTicketDate(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Ticket Number
                </label>
                <input
                  type="text"
                  value={ticketNumber}
                  onChange={(e) => setTicketNumber(e.target.value)}
                  placeholder="Optional"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Hauler Name *
              </label>
              <input
                type="text"
                value={haulerName}
                onChange={(e) => setHaulerName(e.target.value)}
                placeholder="e.g., Waste Management"
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Facility Name *
              </label>
              <input
                type="text"
                value={facilityName}
                onChange={(e) => setFacilityName(e.target.value)}
                placeholder="e.g., Cold Canyon Landfill"
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Destination Type *
              </label>
              <select
                value={destinationType}
                onChange={(e) => setDestinationType(e.target.value as DestinationType)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              >
                <option value="">Select destination</option>
                {DESTINATION_TYPES.map((dest) => (
                  <option key={dest.value} value={dest.value}>
                    {dest.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Weight & Material */}
          <div className="space-y-4 border-t pt-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-neutral-900">Weight & Material</h3>
              <button
                type="button"
                onClick={() => setMode(mode === 'simple' ? 'advanced' : 'simple')}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                {mode === 'simple' ? 'Switch to Advanced Mode' : 'Switch to Simple Mode'}
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Total Weight *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={totalWeight}
                  onChange={(e) => setTotalWeight(e.target.value)}
                  placeholder="e.g., 4.5"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Unit *
                </label>
                <select
                  value={weightUnit}
                  onChange={(e) => setWeightUnit(e.target.value as WeightUnit)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="tons">Tons</option>
                  <option value="lbs">Pounds (lbs)</option>
                </select>
              </div>
            </div>

            {mode === 'simple' ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Material Type *
                  </label>
                  <input
                    type="text"
                    value={materialType}
                    onChange={(e) => setMaterialType(e.target.value)}
                    placeholder="e.g., C&D, Mixed Waste, Wood"
                    list="material-suggestions"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                  <datalist id="material-suggestions">
                    {COMMON_MATERIALS.map(mat => (
                      <option key={mat} value={mat} />
                    ))}
                  </datalist>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Diversion Percentage (Optional)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={diversionPercentage}
                      onChange={(e) => setDiversionPercentage(e.target.value)}
                      placeholder="Leave empty for destination default"
                      className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <span className="text-neutral-600">%</span>
                  </div>
                  <p className="text-xs text-neutral-500 mt-1">
                    If not specified, will use destination type default
                  </p>
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-neutral-600">
                  Break down the load by material type and percentage. Total must equal 100%.
                </p>

                {materials.map((material, index) => (
                  <div key={index} className="flex gap-2 items-start p-3 bg-neutral-50 rounded-lg">
                    <div className="flex-1 space-y-2">
                      <input
                        type="text"
                        value={material.material_type}
                        onChange={(e) => updateMaterial(index, 'material_type', e.target.value)}
                        placeholder="Material type"
                        className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        required
                      />
                      <div className="flex gap-2 items-center">
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          value={material.percentage || ''}
                          onChange={(e) => updateMaterial(index, 'percentage', parseFloat(e.target.value) || 0)}
                          placeholder="% of load"
                          className="w-24 px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          required
                        />
                        <span className="text-sm text-neutral-600">%</span>
                        <label className="flex items-center gap-2 ml-4">
                          <input
                            type="checkbox"
                            checked={material.is_diverted}
                            onChange={(e) => updateMaterial(index, 'is_diverted', e.target.checked)}
                            className="rounded border-neutral-300"
                          />
                          <span className="text-sm text-neutral-700">Diverted</span>
                        </label>
                      </div>
                    </div>
                    {materials.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeMaterial(index)}
                        className="text-red-600 hover:text-red-700 p-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}

                <div className="flex justify-between items-center">
                  <button
                    type="button"
                    onClick={addMaterial}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    + Add Material
                  </button>
                  <div className={`text-sm font-medium ${Math.abs(totalPercentage - 100) < 0.01 ? 'text-green-600' : 'text-orange-600'}`}>
                    Total: {totalPercentage.toFixed(1)}%
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional details..."
              rows={3}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-neutral-300 rounded-lg text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {isLoading ? 'Adding...' : 'Add Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
